import React from "react";
import { useForm, Controller, useWatch, ControllerRenderProps } from "react-hook-form";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import Typography from "@material-ui/core/Typography";

import { GuestPlayerInputs } from "types/player";
import { Gender } from "types/user";
import { CapitalizeWords } from "utils/text";

export interface AddGuestPlayerProps {
  onSubmit: (data: GuestPlayerInputs, cb: () => void) => void;
  defaultValues?: GuestPlayerInputs;
  displayText?: boolean;
}

const AddGuestPlayer: React.FC<AddGuestPlayerProps> = props => {
  const classes = useStyles();
  const { defaultValues, displayText } = props;
  // If defaultValues has socialHcp, do not initialize hcp
  const initialValues = React.useMemo(() => {
    if (defaultValues && defaultValues.socialHcp !== undefined && defaultValues.socialHcp !== null) {
      return { ...defaultValues, hcp: undefined };
    }
    return defaultValues;
  }, [defaultValues]);

  const { control, handleSubmit, reset, watch } = useForm<GuestPlayerInputs>({
    defaultValues: initialValues,
    mode: "onChange",
  });

  const socialHcpValue = useWatch({ control, name: "socialHcp" });

  // If socialHcp is present, disable hcp; if hcp is present, disable socialHcp
  const hcpValue = useWatch({ control, name: "hcp" });
  const isHcpActive = hcpValue !== undefined && hcpValue !== null && hcpValue.toString() !== "";
  const isSocialHcpActive = socialHcpValue !== undefined && socialHcpValue !== null;

  const onSubmit = (formValue: GuestPlayerInputs) => {
    const submissionData: GuestPlayerInputs = {
      ...formValue,
      name: CapitalizeWords(formValue.name),
      lastName: CapitalizeWords(formValue.lastName),
    };

    // Mirror Profile logic: if socialHcp is present, ignore hcp; if hcp present, ignore socialHcp
    if (isSocialHcpActive) {
      submissionData.socialHcp = formValue.socialHcp;
      submissionData.hcp = undefined;
    } else if (formValue.hcp !== undefined && formValue.hcp !== null) {
      submissionData.hcp = formValue.hcp;
      submissionData.socialHcp = undefined;
    } else {
      submissionData.hcp = 0;
      submissionData.socialHcp = undefined;
    }

    props.onSubmit(submissionData, reset.bind(null, defaultValues));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.root} autoComplete="off">
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{ style: { textTransform: "capitalize" } }}
                required
                {...field}
                fullWidth
                label="First name"
                variant="outlined"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{ style: { textTransform: "capitalize" } }}
                required
                {...field}
                fullWidth
                label="Surname"
                variant="outlined"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="hcp"
            control={control}
            render={({ field }: { field: ControllerRenderProps<GuestPlayerInputs, "hcp"> }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                onChange={e => {
                  const value = e.target.value;
                  // Allow empty, minus sign, digits, decimal
                  if (value === "" || /^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(value) || value === "-" || value === "+") {
                    field.onChange(value);
                  }
                }}
                onBlur={() => {
                  const val = field.value?.toString() ?? "";
                  if (val !== "" && val !== "-" && val !== "+") {
                    field.onChange(parseFloat(val));
                  } else {
                    field.onChange(null);
                  }
                }}
                type="text"
                label="Handicap Index"
                variant="outlined"
                fullWidth
                disabled={isSocialHcpActive}
                inputProps={{ inputMode: "text" }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="socialHcp"
            control={control}
            render={({ field }: { field: ControllerRenderProps<GuestPlayerInputs, "socialHcp"> }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                onChange={e => {
                  const value = e.target.value;
                  if (value === "" || /^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(value) || value === "-" || value === "+") {
                    field.onChange(value as any);
                  }
                }}
                onBlur={() => {
                  const val = field.value?.toString() ?? "";
                  if (val !== "" && val !== "-" && val !== "+") {
                    field.onChange(parseFloat(val));
                  } else {
                    field.onChange(null);
                  }
                }}
                type="text"
                label="Social Handicap"
                variant="outlined"
                fullWidth
                disabled={isHcpActive}
                inputProps={{ inputMode: "text" }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="gender"
            control={control}
            render={({ field }) => (
              <FormControl component="fieldset">
                <FormLabel>Gender</FormLabel>
                <RadioGroup {...field} className={classes.group}>
                  <FormControlLabel value="men" control={<Radio />} label="Male" />
                  <FormControlLabel value="women" control={<Radio />} label="Female" />
                </RadioGroup>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          {displayText && (
            <Typography color="secondary">
              Unregistered players details are not retained and they will have no features available to them.
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} className={classes.btnContainer}>
          <Button variant="contained" color="primary" type="submit">
            Submit
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default AddGuestPlayer;

AddGuestPlayer.defaultProps = {
  defaultValues: { name: "", lastName: "", hcp: undefined, socialHcp: undefined, gender: Gender.Men },
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: "1rem 2rem",

      "& p": {
        textAlign: "justify",
      },
    },
    group: {
      flexDirection: "row",
    },
    btnContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
    },
  }),
);
